import type { Project } from '@hangar/shared';
import { stagger } from '@/lib/motion';
import { FormField, fieldA11y } from '@/components/FormField';
import { GatedButton } from '@/components/GatedButton';
import { ProblemAlert } from '@/components/ProblemAlert';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { VISIBLE_LIMITS } from './limitsModel';
import { useLimitsForm, type LimitsForm } from './useLimitsForm';

/**
 * Design 28 · Limits (`PATCH /projects/{id}` `usageLimits`, merge patch: a number sets, `null` clears). Only what works
 * shows: concurrent builds, which the scheduler enforces (builds per day and queued builds are stored, not applied, so
 * they are hidden). Needs admin or the project's maintainer role. Empty means no project limit.
 */
export function LimitsCard({ project, id, index }: { project: Project; id: string; index: number }) {
  const f = useLimitsForm(project);
  return (
    <Card id={id} data-testid="settings-limits" className="enter scroll-mt-24" style={stagger(index)}>
      <form onSubmit={(e) => void f.onSubmit(e)} noValidate aria-label="Limits" className="contents">
        <CardHeader>
          <CardTitle>Limits</CardTitle>
          <CardDescription>How many builds of {project.name} may run at once. Empty means no project limit; your build service's slots still apply.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          {VISIBLE_LIMITS.map((l) => (
            <LimitField key={l.key} limit={l} f={f} />
          ))}
          <ProblemAlert error={f.problem} testId="settings-limits-problem" className="sm:col-span-2" />
        </CardContent>
        {f.can.hidden ? null : <LimitsFooter f={f} />}
      </form>
    </Card>
  );
}

function LimitField({ limit: l, f }: { limit: (typeof VISIBLE_LIMITS)[number]; f: LimitsForm }) {
  const error = f.errorFor(l.key);
  return (
    <FormField id={`limit-${l.key}`} label={l.label} hint={l.hint} error={error}>
      <Input
        data-testid={l.testId}
        inputMode="numeric"
        value={f.values[l.key]}
        readOnly={!f.can.allowed}
        placeholder="No limit"
        onChange={(e) => {
          f.setLimit(l.key, e.target.value);
          f.clearServer(l.key);
        }}
        className="tabular font-mono sm:text-[13px]"
        {...fieldA11y(`limit-${l.key}`, error, `limit-${l.key}-hint`)}
      />
    </FormField>
  );
}

function LimitsFooter({ f }: { f: LimitsForm }) {
  return (
    <CardFooter className="justify-end gap-3">
      {f.dirty ? <span className="text-xs text-subtle">Unsaved changes</span> : null}
      <GatedButton type="submit" variant="outline" data-testid="settings-limits-save" can={f.can} disabled={!f.dirty} loading={f.isPending}>
        Save limits
      </GatedButton>
    </CardFooter>
  );
}
